// ABOUTME: Table component for embedding data tables in reports.
// ABOUTME: Renders tabular data with styling matching the obsidian theme.
// ABOUTME: Supports expand/collapse toggle for large tables via maxDisplayRows prop.

import { ChevronDown, ChevronUp } from 'lucide-react';

/** Default number of rows shown in compact/collapsed state. Matches backend default. */
export const DEFAULT_MAX_DISPLAY_ROWS = 10;

interface TableData {
  headers: string[];
  rows: (string | number)[][];
}

interface ReportTableProps {
  data: TableData;
  title?: string;
  /** When set, truncate the table to this many rows and show an expand/collapse toggle */
  maxDisplayRows?: number;
  /** Called when the user clicks expand/collapse. `showAll=true` means show all rows. */
  onToggleExpand?: (showAll: boolean) => void | Promise<void>;
  /** Disables the toggle button during a pending PATCH */
  toggleDisabled?: boolean;
}

export function ReportTable({ data, title, maxDisplayRows, onToggleExpand, toggleDisabled }: ReportTableProps) {
  if (!data.headers || !data.rows || data.headers.length === 0) {
    return (
      <div className="text-surface-500 text-sm italic p-4 text-center border border-dashed border-surface-200 rounded-sm">
        No table data available
      </div>
    );
  }

  const totalRows = data.rows.length;
  const isTruncated = maxDisplayRows !== undefined && maxDisplayRows < totalRows;
  const displayRows = isTruncated ? data.rows.slice(0, maxDisplayRows) : data.rows;
  const isExpanded = maxDisplayRows === undefined || maxDisplayRows >= totalRows;

  return (
    <div className="w-full overflow-x-auto">
      {title && (
        <h4 className="text-sm font-medium text-surface-600 mb-3">
          {title}
        </h4>
      )}
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {data.headers.map((header, index) => (
              <th
                key={index}
                className="px-4 py-3 text-left text-xs font-semibold text-surface-600 uppercase tracking-wider bg-surface-50 border-b border-surface-200 first:rounded-tl-sm last:rounded-tr-sm"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b border-surface-200 last:border-b-0 hover:bg-surface-50 transition-colors"
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="px-4 py-3 text-sm text-surface-800"
                >
                  {typeof cell === 'number' ? cell.toLocaleString() : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {onToggleExpand && maxDisplayRows !== undefined && (isTruncated || totalRows > DEFAULT_MAX_DISPLAY_ROWS) && (
        <button
          disabled={toggleDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(!isExpanded);
          }}
          className={`w-full mt-1 py-2 text-xs text-surface-500 hover:text-primary-600 hover:bg-surface-50 transition-colors flex items-center justify-center gap-1 rounded-sm cursor-pointer ${toggleDisabled ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {isTruncated ? (
            <>
              <ChevronDown className="w-3.5 h-3.5" />
              Show all {totalRows} rows
            </>
          ) : (
            <>
              <ChevronUp className="w-3.5 h-3.5" />
              Show fewer rows
            </>
          )}
        </button>
      )}
    </div>
  );
}
